import './ReturnOnAdSpend.scss';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  Dispatch,
  SetStateAction,
  FC
} from 'react';
import { useCompanyInfo } from '@src/utils/hooks/useCompanyInfo';
import { WidgetContainer } from '@Components/WidgetContainer';
import { ChartContainer } from '@Components/ChartContainer';
import { Design, Size } from '@Components/designConstants';
import { Column, DimensionColumn } from '@blisspointmedia/bpm-types/dist/MetricsTable';
import { snapShotDataSort, MedianOrMean } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { CONVERSION_FORMATTER_TYPE } from '@Pages/MMM/mmm-utils';
import {
  roasDataFormatter,
  roasSnapshotData
} from '@Components/Charts/ModelResults/DataFormatters';
import { DownloadDropdown } from '@Components/DownloadDropdown';
import { InfoTooltip } from '@Components/InfoTooltip';
import { SnapshotChart } from '@Components/SnapshotChart/SnapshotChart';
import { ToggleSwitch } from '@Components/ToggleSwitch';
import * as R from 'ramda';
import { MetricsTable } from '@Components/MetricsTable/MetricsTable';
import { exportToExcel } from '@src/utils/download-utils';
import { TEST_COMPANIES } from '@blisspointmedia/bpm-types/dist/TestCompanies';
import { RoasData } from '@blisspointmedia/bpm-types/dist/MMM';
import IncrementalityIcon from './Icons/IncrementalityIcon.svg?react';

const DimensionColumns: DimensionColumn[] = [
  {
    id: 'name',
    dimensionVarName: 'Channel',
    label: 'Channel, Platform, Tactic, Brand/Non-Brand',
    dimensionTypeName: 'Channel',
    divider: false
  }
];

const COLUMNS_TO_EXPORT_XLSX = [
  'client',
  'kpi',
  'refresh_date',
  'name',
  'previous_qtr_median_spend',
  'next_dollar_effectiveness'
];

const TABLE_HEADERS: Column[] = [
  {
    id: 'previous_qtr_roas',
    dataVarName: 'roas',
    label: 'Previous quarter ROAS'
  },
  {
    id: 'previous_qtr_cpa',
    dataVarName: 'roas',
    label: 'Previous quarter CPA'
  },
  {
    id: 'all_time_roas',
    dataVarName: 'roas',
    label: 'Entire time period ROAS'
  },
  {
    id: 'all_time_cpa',
    dataVarName: 'roas',
    label: 'Entire time period CPA'
  },
  {
    id: 'previous_qtr_median_spend',
    dataVarName: 'roas',
    label: 'Previous quarter mean spend'
  },
  {
    id: 'response_previous_qtr_median_spend',
    dataVarName: 'roas',
    label: 'Predicted response to mean spend'
  },
  {
    id: 'next_dollar_effectiveness',
    dataVarName: 'spend',
    label: 'Next Dollar Effectiveness',
    heatMapping: {
      colorScheme: 'sequential'
    } as any
  }
];

const ROW_HEIGHT = 50;
const TABLE_HEIGHT = 320;
interface ReturnOnAdSpendProps {
  disabledPlatform: boolean | undefined;
  kpiType: string;
  data: RoasData[];
  renderOmitDarkInputsToggle: boolean;
  omitDarkInputs: boolean;
  setOmitDarkInputs: Dispatch<SetStateAction<boolean>>;
  centralMeasure: MedianOrMean;
}

export const ReturnOnAdSpend: FC<ReturnOnAdSpendProps> = ({
  disabledPlatform,
  kpiType,
  data,
  renderOmitDarkInputsToggle,
  omitDarkInputs,
  setOmitDarkInputs,
  centralMeasure
}) => {
  let { cid: company } = useCompanyInfo();

  const [roasData, setRoasData] = useState([] as any[]);
  const [dimensionData, setDimensionData] = useState([] as any[]);
  const [snapChartData, setSnapChartData] = useState([] as any[]);
  const [snapChartSortValue, setSnapChartSortValue] = useState('Highest to lowest');
  const [tableWidth, setTableWidth] = useState(500);
  const [rerenderTable, setRerenderTable] = useState(false);
  const [tableHeaders, setTableHeaders] = useState<Column[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const companyEqualsTest = TEST_COMPANIES.includes(company);
  if (companyEqualsTest) {
    company = 'test';
  }
  // Re-measure the table on viewport resize. Use addEventListener + cleanup (not `window.onresize =`,
  // which clobbers any other app-wide resize handler).
  useEffect(() => {
    const handleResize = () => setRerenderTable(true);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useLayoutEffect(() => {
    const newTableWidth = containerRef.current?.offsetWidth;
    if (newTableWidth) {
      setTableWidth(newTableWidth - 5);
    }
    if (rerenderTable) {
      setRerenderTable(false);
    }
  }, [rerenderTable]);

  useEffect(() => {
    if (disabledPlatform !== undefined) {
      const nextTableHeaders = TABLE_HEADERS.filter(item =>
        kpiType === 'revenue' ? !item.id.includes('cpa') : !item.id.includes('roas')
      ).map(item => {
        let mappedItem = item;
        if (kpiType !== 'revenue' && mappedItem.label === 'Predicted response to median spend') {
          mappedItem = { ...mappedItem, label: 'Predicted conversions for median spend' };
        }
        if (mappedItem.id.includes('median') && centralMeasure === MedianOrMean.MEAN) {
          mappedItem = {
            ...mappedItem,
            id: mappedItem.id.replace('median', 'mean'),
            label: mappedItem.label!.replace('median', 'mean')
          };
        }
        return mappedItem;
      });
      const formattedRoasData = roasDataFormatter({
        data,
        tableHeaders: nextTableHeaders,
        disabledPlatform,
        companyEqualsTest,
        kpiType
      });
      const formattedDimensionData = roasDataFormatter({
        data,
        tableHeaders: DimensionColumns,
        disabledPlatform,
        companyEqualsTest,
        kpiType
      });
      const snapData = roasSnapshotData(data, disabledPlatform, companyEqualsTest);

      setTableHeaders(nextTableHeaders);
      setSnapChartData(snapShotDataSort(snapData, snapChartSortValue));
      setRoasData(formattedRoasData);
      setDimensionData(formattedDimensionData);
    }
  }, [
    company,
    companyEqualsTest,
    kpiType,
    disabledPlatform,
    snapChartSortValue,
    data,
    centralMeasure
  ]);

  const excelDownloadROAS = useCallback(() => {
    exportToExcel(data, `${company}_${kpiType === 'revenue' ? 'ROAS' : 'CPA'}`);
  }, [company, data, kpiType]);

  return (
    <WidgetContainer
      footer={
        <div className='mmm-widget-bottom-wrapper'>
          <IncrementalityIcon className='incrementality-icon-bottom' />
          <div>
            Channels marked with a green beaker use informative priors based on incrementality
            studies. Incrementality testing is strongly recommended, and we encourage you to contact
            your team to review options.
          </div>
        </div>
      }
      collapsible
      header='Where should we allocate our next media dollar?'
      subHeader={
        <>
          <span>
            Below is the <i className='marginal-emphasis'>marginal</i> impact of each media dollar,
            given the existing allocation of resources.
          </span>
          <InfoTooltip rightLabel='More info' size={Size.SM}>
            The marginal impact of a media dollar is based on where one is on the channel saturation
            curve (see section below). Next dollar effectiveness is the slope of the line tangent to
            the saturation curve at the current level of investment. Intuitively, you want to invest
            where the slope of the curve is steepest, i.e. offers the highest return.
          </InfoTooltip>
        </>
      }
      rightActions={
        renderOmitDarkInputsToggle ? (
          <ToggleSwitch
            design={Design.NEUTRAL}
            size={Size.LG}
            label='Omit Dark Inputs'
            checked={omitDarkInputs}
            onChange={() => {
              setOmitDarkInputs(current => !current);
            }}
            infoTooltipText='Dark inputs are defined as paid media features that account for <1% of spend in the previous quarter. Omitting these inputs allows for focus on active investments only.'
          />
        ) : undefined
      }
    >
      <div className='roas-section'>
        <div className='left'>
          {!R.isEmpty(snapChartData) && (
            <SnapshotChart
              title='Estimated Return on Next Invested Dollar'
              data={snapChartData}
              dropdownOptions={[
                { value: 'Alphabetical' },
                { value: 'Highest to lowest' },
                { value: 'Lowest to highest' }
              ]}
              dropdownValue={snapChartSortValue}
              setDropdownValue={setSnapChartSortValue}
              valueFormatter={value =>
                value ? CONVERSION_FORMATTER_TYPE[kpiType].format(value) : '--'
              }
              exportFormattedData={R.map(
                R.pick(
                  COLUMNS_TO_EXPORT_XLSX.map(column =>
                    column === 'previous_qtr_median_spend' && centralMeasure === MedianOrMean.MEAN
                      ? column.replace('median', 'mean')
                      : column
                  )
                ),
                data
              )}
              pngDownloadClassName='.roas-section .left .snapshot-graph-contents'
            />
          )}
        </div>
        <div className='right'>
          <ChartContainer
            viewRightActions
            title={`${kpiType === 'revenue' ? 'ROAS' : 'CPA'} and Next Best Dollar`}
            rightActions={
              <DownloadDropdown menuOptions={['XLSX']} onClickOptions={[excelDownloadROAS]} />
            }
          >
            <div ref={containerRef} className='roas-table-wrapper'>
              {!R.isEmpty(roasData) &&
                !R.isEmpty(dimensionData) &&
                !R.isEmpty(tableHeaders) &&
                !rerenderTable && (
                  <MetricsTable
                    aggregateData={[]}
                    dimensionColumns={DimensionColumns}
                    dimensionData={dimensionData}
                    tableData={roasData}
                    dataColumns={tableHeaders}
                    overrideParams={{
                      maxColWidth: 130,
                      rowHeight: ROW_HEIGHT,
                      headerTextHeight: 53
                    }}
                    fixedWidth={tableWidth}
                    maxHeight={TABLE_HEIGHT}
                    columnMetaDataMap={{
                      spend: {
                        prettyName: 'Next Dollar Effectiveness',
                        overlayText:
                          'Formally, this is the slope of the line tangent to the response\nsaturation curve at the current level of investment for the channel.',
                        overlayPlacement: 'left'
                      }
                    }}
                  />
                )}
            </div>
          </ChartContainer>
        </div>
      </div>
    </WidgetContainer>
  );
};
